import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

const POLL_MAX = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") { setStatus("error"); return; }
      } catch {
        setStatus("error"); return;
      }
      attempts += 1;
      if (attempts >= POLL_MAX) { setStatus("timeout"); return; }
      setTimeout(poll, 1800);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, clear]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        className="max-w-md w-full text-center border rounded-sm p-12"
        style={{ borderColor: "var(--ggc-border)", background: "var(--ggc-surface)" }}
      >
        {status === "checking" && (
          <>
            <Loader2 size={44} className="text-gold mx-auto animate-spin" strokeWidth={1.3} />
            <h1 className="font-serif-ed text-3xl mt-6">Confirming your order</h1>
            <p className="font-body text-[var(--ggc-muted)] mt-3">Hang tight while we verify your payment…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-gold mx-auto" strokeWidth={1.3} />
            <h1 className="font-serif-ed text-4xl mt-6">Order Confirmed</h1>
            <p className="font-body text-[var(--ggc-muted)] mt-3">
              Thank you. A specialist will contact you shortly to arrange your complimentary
              nationwide delivery.
            </p>
            <Link to="/" className="inline-block mt-8 bg-gold text-black font-label uppercase tracking-[0.2em] text-sm px-8 py-4 rounded-sm hover:bg-[var(--ggc-gold-hover)] transition-colors" data-testid="success-home-btn">
              Back To Home
            </Link>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <XCircle size={48} className="text-gold mx-auto" strokeWidth={1.3} />
            <h1 className="font-serif-ed text-3xl mt-6">
              {status === "timeout" ? "Still Processing" : "Something Went Wrong"}
            </h1>
            <p className="font-body text-[var(--ggc-muted)] mt-3">
              {status === "timeout"
                ? "Your payment is taking a moment to confirm. We'll email you once it's complete."
                : "We couldn't confirm this session. If you were charged, please contact us."}
            </p>
            <Link to="/" className="inline-block mt-8 border font-label uppercase tracking-[0.2em] text-sm px-8 py-4 rounded-sm hover:border-[var(--ggc-gold)] hover:text-gold transition-colors" style={{ borderColor: "var(--ggc-border)" }}>
              Back To Home
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
